import { useEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { StayCategory } from '../../../__generated__/graphql';

export function categoryLabel(category: StayCategory): string {
  const special: Partial<Record<StayCategory, string>> = {
    [StayCategory.AmazingViews]: 'Amazing views',
    [StayCategory.TinyHomes]: 'Tiny homes',
  };
  if (special[category]) return special[category] as string;
  const word = category.toLowerCase().replace(/_/g, ' ');
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Line icons in Airbnb's category-bar style: 24px, 1.5px strokes, no fill. */
const PATHS: Record<StayCategory, ReactNode> = {
  [StayCategory.Trending]: (
    <path d="M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2 1-3.5 2-4.5 0 2 1 3 2 3 0-3-1-5.5 1-8.5z" />
  ),
  [StayCategory.AmazingViews]: (
    <>
      <path d="M2 19 9 9l4 5 3-3 6 8z" />
      <circle cx="17" cy="6" r="2" />
    </>
  ),
  [StayCategory.Beachfront]: (
    <>
      <path d="M3 20c2 0 2-1.2 4.5-1.2S10 20 12 20s2-1.2 4.5-1.2S19 20 21 20" />
      <path d="M12 17V6" />
      <path d="M5 8a7 4 0 0 1 14 0z" />
    </>
  ),
  [StayCategory.Cabins]: (
    <>
      <path d="M3 11 12 4l9 7" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M5 13h14M5 16.5h14M10 20v-4h4v4" />
    </>
  ),
  [StayCategory.Lakefront]: (
    <>
      <path d="M2 16c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1" />
      <path d="M2 20c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1" />
      <path d="M7 12V5l7 7" />
    </>
  ),
  [StayCategory.TinyHomes]: (
    <>
      <path d="M6 12 12 7l6 5" />
      <path d="M8 10.5V18h8v-7.5M11 18v-3h2v3" />
    </>
  ),
  [StayCategory.Countryside]: (
    <>
      <path d="M2 18c4-5 8-5 10-2 2-4 6-5 10-1" />
      <path d="M2 21h20" />
      <path d="M7 12V8M5 9.5 7 7l2 2.5" />
    </>
  ),
  [StayCategory.City]: (
    <>
      <path d="M3 21V9h6v12M9 21V4h7v17M16 21v-9h5v9M2 21h20" />
      <path d="M11.5 8h2M11.5 12h2M11.5 16h2" />
    </>
  ),
  [StayCategory.Design]: (
    <>
      <path d="M4 20 12 4l8 16z" />
      <path d="M8 12h8" />
    </>
  ),
  [StayCategory.Luxe]: (
    <>
      <path d="M6 4h12l3 5-9 11L3 9z" />
      <path d="M3 9h18M9 4l3 16 3-16" />
    </>
  ),
  [StayCategory.Camping]: (
    <>
      <path d="M3 20 12 5l9 15z" />
      <path d="M12 20l-3-6h6z" />
      <path d="M10 3l2 2 2-2" />
    </>
  ),
  [StayCategory.Farms]: (
    <>
      <path d="M4 20V10l5-4 5 4v10" />
      <path d="M4 20h17M14 13h7v7M7 20v-5h4v5" />
    </>
  ),
  [StayCategory.Islands]: (
    <>
      <path d="M3 19c3-2 15-2 18 0" />
      <path d="M12 18c0-5 1-8 3-10" />
      <path d="M15 8c-3-2-6-1-7 1M15 8c1-3 4-4 6-3M15 8c3 0 5 2 5 4" />
    </>
  ),
  [StayCategory.Skiing]: (
    <>
      <circle cx="16" cy="5" r="1.8" />
      <path d="M4 14l16 6M8 9l5 2-1.5 4 4 2" />
    </>
  ),
  [StayCategory.Treehouses]: (
    <>
      <path d="M12 21V12" />
      <path d="M12 3c4 0 7 3 7 6s-3 4-7 4-7-1-7-4 3-6 7-6z" />
      <path d="M8 14h8v4H8z" />
    </>
  ),
};

/** Airbnb's order: trending first, then the popular ones. */
export const CATEGORY_ORDER: StayCategory[] = [
  StayCategory.Trending,
  StayCategory.AmazingViews,
  StayCategory.Beachfront,
  StayCategory.Cabins,
  StayCategory.Lakefront,
  StayCategory.TinyHomes,
  StayCategory.Countryside,
  StayCategory.City,
  StayCategory.Design,
  StayCategory.Luxe,
  StayCategory.Camping,
  StayCategory.Farms,
  StayCategory.Islands,
  StayCategory.Skiing,
  StayCategory.Treehouses,
].filter(category => (Object.values(StayCategory) as string[]).includes(category));

export function CategoryIcon({ category, size = 24 }: { category: StayCategory; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[category]}
    </svg>
  );
}

const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const ScrollBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const Scroller = styled.div`
  display: flex;
  gap: 32px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Item = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 4px 0 12px;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? 'var(--color-text-primary)' : 'transparent')};
  background: transparent;
  color: ${({ $active }) =>
    $active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  white-space: nowrap;
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0.78)};
  transition:
    opacity 0.15s ease,
    border-color 0.15s ease;
  &:hover {
    opacity: 1;
    color: var(--color-text-primary);
    border-bottom-color: ${({ $active }) =>
      $active ? 'var(--color-text-primary)' : 'var(--color-border)'};
  }
`;

const Arrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-60%);
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  box-shadow: 0 0 0 8px var(--color-bg-elevated, #fff);
  cursor: pointer;
  z-index: 1;
  &:hover {
    box-shadow:
      0 0 0 8px var(--color-bg-elevated, #fff),
      0 2px 6px rgba(0, 0, 0, 0.18);
  }
`;

function Chevron({ left }: { left?: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      aria-hidden="true"
    >
      <path d={left ? 'M10 2 4 8l6 6' : 'M6 2l6 6-6 6'} />
    </svg>
  );
}

/** Horizontal arrows that appear only when there is more to scroll that way. */
export function useScrollArrows<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () =>
      setEdges({
        left: el.scrollLeft > 4,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const scroll = (direction: 1 | -1) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  return { ref, edges, scroll };
}

/** The icon row under the search bar (spec 016 W11); tapping the active one clears it. */
export function CategoryBar({
  value,
  onChange,
  trailing,
}: {
  value?: StayCategory;
  onChange: (category?: StayCategory) => void;
  /** Shown at the right end, e.g. the Filters button on the results page. */
  trailing?: ReactNode;
}) {
  const { ref, edges, scroll } = useScrollArrows<HTMLDivElement>();
  return (
    <Wrap>
      <ScrollBox>
        {edges.left && (
          <Arrow
            type="button"
            $side="left"
            aria-label="Previous categories"
            onClick={() => scroll(-1)}
          >
            <Chevron left />
          </Arrow>
        )}
        <Scroller ref={ref} role="tablist" aria-label="Categories">
          {CATEGORY_ORDER.map(category => (
            <Item
              key={category}
              type="button"
              role="tab"
              aria-selected={value === category}
              $active={value === category}
              onClick={() => onChange(value === category ? undefined : category)}
            >
              <CategoryIcon category={category} />
              {categoryLabel(category)}
            </Item>
          ))}
        </Scroller>
        {edges.right && (
          <Arrow type="button" $side="right" aria-label="More categories" onClick={() => scroll(1)}>
            <Chevron />
          </Arrow>
        )}
      </ScrollBox>
      {trailing}
    </Wrap>
  );
}
